/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, ReactElement, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Box, CircularProgress } from "@mui/material";
import DisplayAmount from "../../shared/DisplayAmount";
import axios from "axios";
import { Tooltip, IconButton } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { MdOutlineCancel } from "react-icons/md";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { MdRemoveCircleOutline } from "react-icons/md";
import { ProfileContext } from "../../../../store/ProfileProvider";
import SweetAlert2 from "react-sweetalert2";
import RequestWhitelistAddressApproveConfirmModal from "../../shared/RequestWhitelistAddressApproveConfirmModal";
import RequestWhitelistAddressRejectConfirmModal from "../../shared/RequestWhitelistAddressRejectConfirmModal";
import RequestWhitelistAddressBlacklistConfirmModal from "../../shared/RequestWhitelistAddressBlacklistConfirmModal";
import AgGridPagination from "../../shared/AgGridPagination";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type Merchant = {
  id: string;
  name: string;
};

type Subaccount = {
  id: string;
  name: string;
};

type RowData = {
  wlad_id: string;
  status: string;
  address: string;
  asset: string;
  created_at: string;
  reason?: string;
  merchant: Merchant;
  subaccount: Subaccount;
};

interface PayloadType {
  [key: string]: any;
}

const rowPerPage = 10;

const AccountBalancesTab: React.FC<{ isImpersonating?: boolean }> = ({ isImpersonating }) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [swalProps, setSwalProps] = useState<any>({});
  const { subaccount_id } = useParams();

  const [whitelistAddressToApprove, setWhitelistAddressToApprove] =
    useState<any>(null);
  const [
    isOpenWhitelistAddressApproveConfirmModal,
    setIsOpenWhitelistAddressApproveConfirmModal,
  ] = useState<boolean>(false);

  // Reject modal states
  const [whitelistAddressToReject, setWhitelistAddressToReject] =
    useState<any>(null);
  const [
    isOpenWhitelistAddressRejectConfirmModal,
    setIsOpenWhitelistAddressRejectConfirmModal,
  ] = useState<boolean>(false);

  // Blacklist modal states
  const [whitelistAddressToBlacklist, setWhitelistAddressToBlacklist] =
    useState<any>(null);
  const [
    isOpenWhitelistAddressBlacklistConfirmModal,
    setIsOpenWhitelistAddressBlacklistConfirmModal,
  ] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await fetchWhitelistAddresses({
        page: 0,
        limit: rowPerPage,
        lastkey: "",
      });
      setIsLoading(false);
    };

    fetchData();
  }, []); // eslint-disable-line

  const fetchWhitelistAddresses = async (payload: PayloadType = {}) => {
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey,
        subaccount_id: subaccount_id,
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/trade/whitelist-addresses?${queryString}`
      );

      let newData = response.data.whitelist_addresses?.payload;

      if (newData && newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      setIsLoading(false);

      // Manage Last Key and Total Record
      let newLastKey = response.data.whitelist_addresses?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      }
    } catch (error) {
      setTableData([]);
      setIsLoading(false);
      // Clear all
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      subaccount_id: subaccount_id,
    };

    // If click prev pagination
    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        subaccount_id: subaccount_id,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    fetchWhitelistAddresses(newFilters);
  };

  interface WhitelistAddressestatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: WhitelistAddressestatusItems[] = [
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "approved",
      value: "approved",
      display: (
        <Badge className={`${statusClasses} status-approved`}>Approved</Badge>
      ),
    },
    {
      status: "rejected",
      value: "rejected",
      display: (
        <Badge className={`${statusClasses} status-rejected`}>Rejected</Badge>
      ),
    },
    {
      status: "cancelled",
      value: "cancelled",
      display: (
        <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
      ),
    },
    {
      status: "blacklist",
      value: "blacklist",
      display: (
        <Badge className={`${statusClasses} status-blacklist`}>Blacklist</Badge>
      ),
    },
  ];

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  const CreatedDateRenderer = (params: ICellRendererParams) => (
    <>{formatDate(params.data.created_at)}</>
  );

  const SubaccountRenderer = (params: ICellRendererParams) => (
    <>{params.data.subaccount?.name}</>
  );

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.data.status;
    const statusItem = statusList.find((item) => item.status === status);
    return (
      <>
        {(status === "rejected" || status === "blacklist") &&
        params.data.reason ? (
          <Tooltip title={params.data.reason} arrow placement="top">
            <div className="cursor-help">
              {statusItem ? statusItem.display : null}
            </div>
          </Tooltip>
        ) : (
          <>{statusItem ? statusItem.display : null}</>
        )}
      </>
    );
  };

  const ActionsRenderer = (params: ICellRendererParams) => {
    const { status } = params.data || {};
    const canApprove = status === "pending";
    const canReject = status === "pending";
    const canBlacklist = status === "approved";
    return (
      <>
        {canApprove && (
          <Tooltip title="Mark as Approve" arrow placement="top">
            <div>
              <IconButton
                id={`button-approve-${params.data?.wlad_id}`}
                onClick={() => handleClickApprove(params.data)}
                size="small"
              >
                <AiOutlineCheckCircle className="status-approved h-5 w-5" />
              </IconButton>
            </div>
          </Tooltip>
        )}
        {canReject && (
          <Tooltip title="Mark as Reject" arrow placement="top">
            <div>
              <IconButton
                id={`button-reject-${params.data?.wlad_id}`}
                onClick={() => handleClickReject(params.data)}
                size="small"
              >
                <MdOutlineCancel className="status-rejected h-5 w-5" />
              </IconButton>
            </div>
          </Tooltip>
        )}
        {canBlacklist && (
          <Tooltip title="Mark as Blacklist" arrow placement="top">
            <div>
              <IconButton
                id={`button-blacklist-${params.data?.wlad_id}`}
                onClick={() => handleClickBlacklist(params.data)}
                size="small"
              >
                <MdRemoveCircleOutline className="status-rejected h-5 w-5" />
              </IconButton>
            </div>
          </Tooltip>
        )}
      </>
    );
  };

  const columns: ColDef[] = [
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      width: 180,
      cellRenderer: CreatedDateRenderer,
    },
    {
      field: "subaccount",
      headerName: "Subaccount",
      sortable: false,
      width: 160,
      cellRenderer: SubaccountRenderer,
    },
    {
      field: "nickname",
      headerName: "Nickname",
      sortable: false,
      width: 180,
    },
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      width: 80,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "network",
      headerName: "Network",
      sortable: false,
      width: 140,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "address",
      headerName: "Address",
      sortable: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      width: 140,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 120,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  // Approve handlers
  const handleClickApprove = (whitelistAddress: any) => {
    setWhitelistAddressToApprove(whitelistAddress);
    showWhitelistAddressApproveConfirmModal();
  };

  const showWhitelistAddressApproveConfirmModal = () => {
    setIsOpenWhitelistAddressApproveConfirmModal(true);
  };

  const closeWhitelistAddressApproveConfirmModal = () => {
    setWhitelistAddressToApprove(null);
    setIsOpenWhitelistAddressApproveConfirmModal(false);
  };

  // Reject handlers
  const handleClickReject = (whitelistAddress: any) => {
    setWhitelistAddressToReject(whitelistAddress);
    showWhitelistAddressRejectConfirmModal();
  };

  const showWhitelistAddressRejectConfirmModal = () => {
    setIsOpenWhitelistAddressRejectConfirmModal(true);
  };

  const closeWhitelistAddressRejectConfirmModal = () => {
    setWhitelistAddressToReject(null);
    setIsOpenWhitelistAddressRejectConfirmModal(false);
  };

  // Blacklist handlers
  const handleClickBlacklist = (whitelistAddress: any) => {
    setWhitelistAddressToBlacklist(whitelistAddress);
    showWhitelistAddressBlacklistConfirmModal();
  };

  const showWhitelistAddressBlacklistConfirmModal = () => {
    setIsOpenWhitelistAddressBlacklistConfirmModal(true);
  };

  const closeWhitelistAddressBlacklistConfirmModal = () => {
    setWhitelistAddressToBlacklist(null);
    setIsOpenWhitelistAddressBlacklistConfirmModal(false);
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const triggerReloadList = async () => {
    setIsLoading(true);
    await fetchWhitelistAddresses({
      page: page,
      limit: rowPerPage,
      lastkey: page !== 0 ? currentLastKey : "",
    });
  };

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <>
      {/* Approve Modal */}
      {isOpenWhitelistAddressApproveConfirmModal &&
        whitelistAddressToApprove && (
          <RequestWhitelistAddressApproveConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenWhitelistAddressApproveConfirmModal}
            dataToApprove={whitelistAddressToApprove}
            onClose={closeWhitelistAddressApproveConfirmModal}
          />
        )}

      {/* Reject Modal */}
      {isOpenWhitelistAddressRejectConfirmModal && whitelistAddressToReject && (
        <RequestWhitelistAddressRejectConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenWhitelistAddressRejectConfirmModal}
          dataToReject={whitelistAddressToReject}
          onClose={closeWhitelistAddressRejectConfirmModal}
        />
      )}

      {/* Blacklist Modal */}
      {isOpenWhitelistAddressBlacklistConfirmModal &&
        whitelistAddressToBlacklist && (
          <RequestWhitelistAddressBlacklistConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenWhitelistAddressBlacklistConfirmModal}
            dataToBlacklist={whitelistAddressToBlacklist}
            onClose={closeWhitelistAddressBlacklistConfirmModal}
          />
        )}
      <div className="px-4 py-8">
        <Box>
          <div style={{ width: "100%" }}>
            <AgGridReact
              theme={themeAlpine}
              rowData={isLoading ? [] : tableData}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              getRowId={(params) => params.data.wlad_id}
              rowHeight={48}
              headerHeight={44}
              domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
              suppressPaginationPanel={true}
              loading={isLoading}
              loadingOverlayComponent={CustomLoadingOverlay}
              overlayNoRowsTemplate="No data available"
            />
          </div>

          <AgGridPagination
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            onPaginationChange={setPaginationModel}
            hidden={hidePagination}
          />
        </Box>

        <SweetAlert2
          {...swalProps}
          customClass={{
            actions: "custom-swal2-popup-action",
            title: "custom-swal2-popup-title",
            confirmButton:
              "custom-swal2-popup-action custom-swal2-popup-action-confirm",
            cancelButton:
              "custom-swal2-popup-action custom-swal2-popup-action-cancel",
          }}
        ></SweetAlert2>
      </div>
    </>
  );
};

export default AccountBalancesTab;
